import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useMe} from '../../../common/hooks/useMe'
import {generateIconZip, type IconComposeState} from '../../../common/utils/iconRenderer'
import type {AppIconPlatformId} from '../../../common/constants/appIconSizes'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {downloadFile} from '../../../common/utils/downloadFile'
import IconExportDialog from './IconExportDialog'

interface IconDownloadButtonProps {
  state: IconComposeState
  selectedPlatforms: ReadonlySet<AppIconPlatformId>
}

const IconDownloadButton = ({state, selectedPlatforms}: IconDownloadButtonProps) => {
  const {t} = useTranslation()
  const {me} = useMe()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Signed-in users with at least one credit get the direct-download fast path —
  // the upsell dialog only targets visitors who haven't paid.
  const hasCredits = me !== null && me.credits > 0

  const handleClick = useCallback(async () => {
    if (!state.sourceImage || selectedPlatforms.size === 0) return
    trackEvent('icon_download_clicked', {
      has_credits: hasCredits,
      platform_count: selectedPlatforms.size,
    })
    if (!hasCredits) {
      setDialogOpen(true)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const blob = await generateIconZip(state, selectedPlatforms)
      await downloadFile(blob, 'app-icons.zip')
      trackEvent('icon_zip_completed', {
        size_bytes: blob.size,
        platform_count: selectedPlatforms.size,
        gated: false,
      })
    } catch {
      setError(t('iconGenerator.download.failed'))
    } finally {
      setBusy(false)
    }
  }, [state, selectedPlatforms, hasCredits, t])

  const handleDialogClose = useCallback(() => setDialogOpen(false), [])

  const disabled = busy || !state.sourceImage || selectedPlatforms.size === 0

  return (
    <div className="icg-download">
      <button type="button" className="icg-btn icg-btn-primary" onClick={handleClick} disabled={disabled}>
        {busy ? t('iconGenerator.download.busy') : t('iconGenerator.download.cta')}
      </button>
      {error && <p className="icg-error">{error}</p>}
      {!state.sourceImage && <p className="icg-hint">{t('iconGenerator.download.needImage')}</p>}
      {state.sourceImage && selectedPlatforms.size === 0 && (
        <p className="icg-hint">{t('iconGenerator.download.needPlatform')}</p>
      )}

      {dialogOpen && (
        <IconExportDialog state={state} selectedPlatforms={selectedPlatforms} onClose={handleDialogClose} />
      )}
    </div>
  )
}

export default IconDownloadButton
